import { useEffect, useState } from 'react';
import { platformApi } from '@/api/platform';
import { brandingAssetBlobUrl } from '@/api/client';
import { useAppContext } from '@/context/AppContextProvider';

/**
 * The resolved branding payload paints the portal with the British spellings
 * core uses in its resolved view (primaryColour, …), while the stored row and
 * the editor speak American (primaryColor, …). The type in src/types/platform
 * models the stored row, so the resolved response is narrowed here instead of
 * polluting that shared type with two spellings of every field.
 */
interface ResolvedBrandingLike {
  primaryColour?: string | null;
  secondaryColour?: string | null;
  accentColour?: string | null;
  portalTitle?: string | null;
  welcomeText?: string | null;
  logoObjectKey?: string | null;
  /**
   * The imported branding package's design tokens. Inert data — its palette
   * drives the CSS variables below, and the remaining tokens (typography,
   * spacing, radii) are preserved for consumers that can express them.
   */
  customCss?: { palette?: PackagePaletteLike } | null;
  /** Object storage key of the package stylesheet, when one was imported. */
  customCssKey?: string | null;
}

interface PackagePaletteLike {
  primary?: string;
  secondary?: string;
  accent?: string;
  loginBackground?: string;
  onPrimary?: string;
}

export interface AppliedBranding {
  primaryColour?: string | null;
  secondaryColour?: string | null;
  accentColour?: string | null;
  portalTitle?: string | null;
  /** A blob URL for the institution's logo, or null while none is set. */
  logoUrl?: string | null;
}

/**
 * The style element the imported package's stylesheet is injected into, and
 * the CSS variables it can drive. The `data` attribute is how this hook finds
 * its own element again on a later effect run, so a second import replaces
 * rather than stacks.
 */
const PACKAGE_STYLE_MARKER = 'data-branding-package';

function applyPackageStyleSheet(css: string): void {
  const previous = document.head.querySelector<HTMLStyleElement>(
    `style[${PACKAGE_STYLE_MARKER}]`,
  );
  previous?.remove();
  const style = document.createElement('style');
  style.setAttribute(PACKAGE_STYLE_MARKER, '');
  style.textContent = css;
  document.head.appendChild(style);
}

/**
 * Fetches the active institution's resolved branding and applies it to the
 * shell: the brand color and its companions become CSS variables on the
 * document root (the portal already reads var(--sapBrandColor) everywhere),
 * the portal title replaces the browser tab's title, and the stored logo is
 * fetched through the authenticated asset route into a blob URL the shell can
 * render in an <img>.
 *
 * When the institution imported a branding package, its palette (from the
 * design tokens) overrides the colour variables, and its stylesheet is fetched
 * through the same asset route and injected as a page-level <style> — the
 * institution's own design applied to its own portal, exactly as core applies
 * it on its own origin.
 *
 * Failure is silent on purpose: theming is decoration. An unreachable branding
 * endpoint must leave the portal on the platform defaults, not on an error
 * screen or a request storm.
 */
export function useBranding(): { branding: AppliedBranding | null } {
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';
  const [branding, setBranding] = useState<AppliedBranding | null>(null);

  useEffect(() => {
    if (!institutionId) return;
    let active = true;
    let createdUrl: string | null = null;

    platformApi.branding
      .resolved(institutionId)
      .then((payload) => {
        if (!active) return;
        const resolved = payload as unknown as ResolvedBrandingLike;
        const next: AppliedBranding = {
          primaryColour: resolved.primaryColour ?? null,
          secondaryColour: resolved.secondaryColour ?? null,
          accentColour: resolved.accentColour ?? null,
          portalTitle: resolved.portalTitle ?? null,
          logoUrl: null,
        };
        setBranding(next);

        // The imported package's palette is the authority over the resolved
        // colours: both were written by the same import, and the package one
        // travels with the tokens that document what each colour is for.
        const palette = resolved.customCss?.palette ?? {};
        const primary = palette.primary ?? resolved.primaryColour;
        const secondary = palette.secondary ?? resolved.secondaryColour;
        const accent = palette.accent ?? resolved.accentColour;

        const root = document.documentElement;
        if (primary) root.style.setProperty('--sapBrandColor', primary);
        if (secondary) root.style.setProperty('--sapBrandSecondaryColor', secondary);
        if (accent) root.style.setProperty('--sapBrandAccentColor', accent);
        if (resolved.portalTitle) document.title = resolved.portalTitle;

        // The stylesheet of an imported package is a branding asset behind the
        // same authenticated route as the logo; fetch its bytes and apply them
        // as the shell's own style element.
        if (resolved.customCssKey) {
          brandingAssetBlobUrl(institutionId, 'custom-css')
            .then((blobUrl) =>
              fetch(blobUrl)
                .then((response) => response.text())
                .then((css) => {
                  createdUrl = blobUrl;
                  if (active) applyPackageStyleSheet(css);
                }),
            )
            .catch(() => {
              // the palette still applies; the stylesheet is decoration too
            });
        }

        // The logo lives behind the same authenticated route the resolved view
        // does, so it is fetched like any other API call and rendered from a blob
        // URL. A missing or failing asset keeps the platform logo in the shell.
        if (resolved.logoObjectKey) {
          brandingAssetBlobUrl(institutionId, 'logo')
            .then((logoUrl) => {
              createdUrl = logoUrl;
              if (active) setBranding((prev) => (prev ? { ...prev, logoUrl } : prev));
            })
            .catch(() => {
              // platform logo stays
            });
        }
      })
      .catch(() => {
        // Platform defaults remain in place; the shell stays usable.
      });

    return () => {
      active = false;
      document.head
        .querySelector<HTMLStyleElement>(`style[${PACKAGE_STYLE_MARKER}]`)
        ?.remove();
      // Object URLs pin the blob in memory until revoked; a long-lived SPA that
      // switched contexts would otherwise leak one URL per switch.
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [institutionId]);

  return { branding };
}